import React, { startTransition, useEffect, useState } from 'react';

import ReactMarkdown from 'react-markdown';

import { ProductUpdate } from '@/js/apps/product_updates/types';
import { truncateMarkdown } from '@/js/apps/product_updates/utils';
import { useOutsideClick } from '@/js/hooks';
import { jsonParseSafe } from '@/js/utils';
import { Icon, IconButton, LightCard, NewFeatureBadge } from '@components';
import productUpdatesData from '@product-updates/compiled.json';

const LOCALSTORAGE_KEY = `product-update-notification`;

interface NotificationState {
  hasNewProductUpdate: boolean;
  lastSeenUpdateId: number;
}

export const ProductUpdateNotification = () => {
  const productUpdates: ProductUpdate[] = productUpdatesData;
  const latestUpdateId = Math.max(...productUpdates.map((update) => update.id));

  const [productUpdateNotificationState, setProductUpdateNotificationState] =
    useState<NotificationState>(
      jsonParseSafe<NotificationState>(
        localStorage.getItem(LOCALSTORAGE_KEY),
      ) ?? {
        hasNewProductUpdate: false,
        lastSeenUpdateId: 0,
      },
    );

  const newUpdatesCount = productUpdates.filter(
    (update) => update.id > productUpdateNotificationState.lastSeenUpdateId,
  ).length;

  const { open, setOpen, ref } = useOutsideClick();

  // Save data to localStorage
  useEffect(() => {
    localStorage.setItem(
      LOCALSTORAGE_KEY,
      JSON.stringify(productUpdateNotificationState),
    );
  }, [productUpdateNotificationState]);

  useEffect(() => {
    if (productUpdates.length > 0) {
      const hasNewUpdate =
        latestUpdateId > productUpdateNotificationState.lastSeenUpdateId;

      if (hasNewUpdate) {
        setProductUpdateNotificationState((prev) => ({
          ...prev,
          hasNewProductUpdate: true,
        }));
      }
    }
  }, []);

  const handleIconButtonClick = () => {
    setOpen((prevState) => !prevState);

    // Mark as seen when user clicks the notification bell
    if (productUpdateNotificationState.hasNewProductUpdate) {
      setProductUpdateNotificationState((prev) => ({
        ...prev,
        hasNewProductUpdate: false,
        lastSeenUpdateId: latestUpdateId,
      }));
    }
  };

  // Show the last 3 updates regardless of what the user has seen
  const productUpdatesToShow: ProductUpdate[] = productUpdates
    .sort((a, b) => b.id - a.id)
    .slice(0, 3);

  return (
    <div className="flex flex-row items-center gap-2">
      <NewFeatureBadge
        id="product-updates"
        hideAfter={new Date('July 11, 2025 13:00:00')}
        text="New feature!"
      />
      <div className="relative" ref={ref}>
        <IconButton
          kind="plain"
          variant="ghost"
          iconName="bell"
          aria-label="Product Updates"
          onClick={handleIconButtonClick}
        />
        {newUpdatesCount > 0 && (
          <div className="bg-sp-error-500 text-sp-white absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold">
            {newUpdatesCount}
          </div>
        )}
        {open && (
          <LightCard className="absolute right-0 z-50 w-[350px]">
            <div className="border-sp-neutral-200 border-b p-3">
              <h2 className="text-sp-neutral text-center text-lg font-semibold">
                Product Updates
              </h2>
            </div>

            {productUpdatesToShow.length > 0 ? (
              <ul className="list-none">
                {productUpdatesToShow.map((update) => (
                  <li
                    key={update.id}
                    className="border-sp-neutral-100 hover:bg-sp-neutral-50 border-b p-4"
                  >
                    <a
                      onClick={() => {
                        startTransition(() => {
                          setOpen(false);
                        });
                      }}
                      href={`/product-updates/${update.id}`}
                    >
                      <p className="text-sp-neutral-900 font-semibold">
                        {update.header}
                      </p>
                      <ReactMarkdown
                        children={truncateMarkdown(update.body)}
                        components={{
                          p: ({ children }) => (
                            <p className="text-sp-neutral-500 line-clamp-3 overflow-hidden truncate text-ellipsis text-sm">
                              {children}
                            </p>
                          ),
                        }}
                      />
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-col items-center justify-center p-4">
                <Icon
                  name="bookOpen"
                  size="2xl"
                  className="text-sp-neutral-500"
                />
                <p className="text-sp-neutral-500">No new updates</p>
              </div>
            )}

            <div className="border-sp-primary-400 text-sp-primary flex justify-center border-t p-4">
              <a href="/product-updates">View all updates</a>
            </div>
          </LightCard>
        )}
      </div>
    </div>
  );
};
